import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { Pill } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import { listUsers } from '../../data/fetchers';
import { canEditSystemConfig } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import type { User } from '../../types/domain';
import { UserFormModal } from './UserFormModal';

const ROLE_TH: Record<User['role'], string> = {
  owner: 'เจ้าของ',
  manager: 'ผู้จัดการ',
  cashier: 'แคชเชียร์',
};

export function UsersView() {
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditSystemConfig(user) : false;
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
  });
  const [editing, setEditing] = useState<User | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (u: User) => {
    setEditing(u);
    setModalOpen(true);
  };

  const columns = useMemo<ColumnDef<User, unknown>[]>(
    () => [
      { accessorKey: 'name', header: 'ชื่อ' },
      { accessorKey: 'phone', header: 'เบอร์โทร' },
      {
        accessorKey: 'role',
        header: 'บทบาท',
        cell: (c) => ROLE_TH[c.getValue<User['role']>()],
      },
      {
        id: 'branches',
        header: 'สาขา',
        cell: (c) => c.row.original.branchIds.length || 'ทั้งหมด',
      },
      {
        accessorKey: 'active',
        header: 'สถานะ',
        cell: (c) =>
          c.getValue<boolean>() ? (
            <Pill tone="ok">ใช้งาน</Pill>
          ) : (
            <Pill tone="neutral">ปิด</Pill>
          ),
      },
      {
        id: 'pin',
        header: 'PIN',
        cell: () => <span className="num text-ink-3">••••</span>,
      },
      {
        id: 'go',
        header: '',
        cell: (c) => (
          <button
            className="text-primary text-sm-label"
            onClick={() => openEdit(c.row.original)}
          >
            แก้ไข
          </button>
        ),
      },
    ],
    [],
  );

  if (!canEdit) {
    return (
      <div className="bo-fade">
        <PageHeader title="ผู้ใช้และสิทธิ์" />
        <div className="bo-card">
          <EmptyState
            title="เฉพาะเจ้าของ"
            sub="ผู้ใช้และสิทธิ์จัดการได้โดยผู้มีสิทธิ์ระบบเท่านั้น"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bo-fade">
      <PageHeader
        title="ผู้ใช้และสิทธิ์"
        subtitle={`${users.length} ผู้ใช้`}
        actions={
          <Btn kind="primary" onClick={openAdd}>
            + เพิ่มผู้ใช้
          </Btn>
        }
      />
      <DataTable columns={columns} data={users} />
      <UserFormModal
        open={modalOpen}
        user={editing}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
